import { useMemo, useRef } from "react"

// Reorders items so the ones that were selected when the popover opened
// sit first, with `divider` pointing at the first non-pinned index. The
// snapshot is taken on each false→true transition of `isActive` (popover
// reopen) — Combobox.Root stays mounted across opens, so a useRef-only
// snapshot would freeze forever and ignore the user's latest selection.
// Returns items untouched (and divider = -1) when disabled or when the
// snapshot was empty.
//
// Used by Combobox.List (via the `pinSelected` Root prop) and directly by
// MultiValuePicker — the two paths share this so the UX stays in sync.
const useInitialSelectedFirst = <T, >(
  items: T[],
  getKey: (item: T) => string,
  initialKeys: Set<string>,
  enabled: boolean,
  isActive: boolean,
): { items: T[]; divider: number } => {
  const snapshotRef = useRef(initialKeys)
  const wasActiveRef = useRef(isActive)
  if (!wasActiveRef.current && isActive) {
    snapshotRef.current = initialKeys
  }
  wasActiveRef.current = isActive

  return useMemo(() => {
    if (!enabled || snapshotRef.current.size === 0) {
      return { items, divider: -1 }
    }
    const pinned: T[] = []
    const rest: T[] = []
    items.forEach((item) => {
      if (snapshotRef.current.has(getKey(item))) pinned.push(item)
      else rest.push(item)
    })

    return {
      items: [...pinned, ...rest],
      divider: pinned.length > 0 && rest.length > 0 ? pinned.length : -1,
    }
  }, [items, getKey, enabled])
}

export default useInitialSelectedFirst
